import {
  ANALYZE_ABSENCE_IMPORT_MUTATION,
  EXECUTE_ABSENCE_IMPORT_MUTATION,
  PREPARE_ABSENCE_IMPORT_MUTATION,
} from '@/modules/timesheets/mutations'
import { ALL_ABSENCE_TYPES_QUERY } from '@/modules/timesheets/queries'
import { toast } from '@/components/toast'
import { useFormWatch, useOnErrorAuto } from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { Activity, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'
import { Section } from '../../common'
import {
  StepColumnMapping,
  StepSummary,
  StepTypeMapping,
  StepUpload,
} from './components'
import type {
  AbsenceImportFormValues,
  AnalyzePayload,
  ExecutePayload,
} from './types'

const STEP_UPLOAD = 1
const STEP_COLUMNS = 2
const STEP_TYPES = 3
const STEP_SUMMARY = 4

type AbsenceTypeOption = {
  id: string
  name: string
  externalId?: string | null
}

const AbsenceImportFormSchema = z.object({
  selectedFile: z.custom<File | null>(value => {
    return (
      value === null ||
      (typeof File !== 'undefined' && value instanceof File)
    )
  }),
  sessionId: z.string().nullable(),
  sheetName: z.string(),
  headerRow: z.number().nullable(),
  columns: z.array(
    z.object({
      index: z.number(),
      label: z.string(),
    })
  ),
  previewRows: z.array(
    z.object({
      sourceRow: z.number(),
      values: z.array(z.string()),
    })
  ),
  parseWarnings: z.array(z.string()),
  columnMapping: z.object({
    employeeNumberColumn: z.number().nullable(),
    absenceTypeColumn: z.number().nullable(),
    fromDateColumn: z.number().nullable(),
    toDateColumn: z.number().nullable(),
  }),
  analysis: z.custom<AnalyzePayload | null>(() => true),
  typeMappings: z.array(
    z.object({
      importedValue: z.string(),
      absenceTypeId: z.string(),
    })
  ),
  options: z.object({
    setApproved: z.boolean(),
    updateBalance: z.boolean(),
    importAnyway: z.boolean(),
  }),
  executeResult: z.custom<ExecutePayload | null>(() => true),
  localError: z.string(),
})

const defaultValues: AbsenceImportFormValues = {
  selectedFile: null,
  sessionId: null,
  sheetName: '',
  headerRow: null,
  columns: [],
  previewRows: [],
  parseWarnings: [],
  columnMapping: {
    employeeNumberColumn: null,
    absenceTypeColumn: null,
    fromDateColumn: null,
    toDateColumn: null,
  },
  analysis: null,
  typeMappings: [],
  options: {
    setApproved: true,
    updateBalance: true,
    importAnyway: false,
  },
  executeResult: null,
  localError: '',
}

const COLUMN_SYNONYMS = {
  employeeNumber: [
    'ansnr',
    'ansattnr',
    'ansattnummer',
    'employeeid',
    'employeenumber',
    'internalnumber',
    'personnelnumber',
  ],
  absenceType: [
    'fravaerstype',
    'fravarstype',
    'absencetype',
    'typefravaer',
    'typeabsence',
  ],
  fromDate: ['fradato', 'fromdate', 'startdate', 'start'],
  toDate: ['tildato', 'todate', 'enddate', 'end'],
} as const

function normalizeImportText(value: string | null | undefined) {
  return (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
}

function parseImportDateLike(value: string | null | undefined) {
  const text = (value ?? '').trim()
  if (!text) return null

  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(text)) return text
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text
  if (/^\d{4}\/\d{2}\/\d{2}$/.test(text)) return text
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(text)) return text

  const timestamp = Date.parse(text)
  return Number.isNaN(timestamp) ? null : text
}

function looksLikeEmployeeNumber(value: string | null | undefined) {
  const compact = (value ?? '').replace(/[\s\-._/]/g, '')
  return /^\d{1,20}$/.test(compact)
}

function scoreColumnByHeaderAndValues(
  label: string,
  values: string[],
  synonyms: readonly string[],
  kind: 'employeeNumber' | 'absenceType' | 'date'
) {
  const normalizedLabel = normalizeImportText(label)
  let score = 0

  if (synonyms.includes(normalizedLabel)) score += 120
  else if (synonyms.some(synonym => normalizedLabel.includes(synonym))) score += 65

  const nonEmptyValues = values.filter(Boolean)
  if (!nonEmptyValues.length) return score

  if (kind === 'employeeNumber') {
    const numericLike = nonEmptyValues.filter(looksLikeEmployeeNumber).length
    score += (numericLike / nonEmptyValues.length) * 30
  }

  if (kind === 'absenceType') {
    const textLike = nonEmptyValues.filter(value => {
      return !looksLikeEmployeeNumber(value) && parseImportDateLike(value) === null
    }).length
    score += (textLike / nonEmptyValues.length) * 25
  }

  if (kind === 'date') {
    const dateLike = nonEmptyValues.filter(value => parseImportDateLike(value) !== null)
      .length
    score += (dateLike / nonEmptyValues.length) * 35
  }

  return score
}

function buildSmartColumnMapping(
  columns: AbsenceImportFormValues['columns'],
  previewRows: AbsenceImportFormValues['previewRows'],
  suggestedMapping: AbsenceImportFormValues['columnMapping']
): AbsenceImportFormValues['columnMapping'] {
  const sampleValuesByColumn = columns.map((_, index) =>
    previewRows
      .map(row => row.values[index] ?? '')
      .filter(value => value.trim() !== '')
      .slice(0, 20)
  )

  const scores = columns.map((column, index) => ({
    index: column.index,
    employeeNumber: scoreColumnByHeaderAndValues(
      column.label,
      sampleValuesByColumn[index],
      COLUMN_SYNONYMS.employeeNumber,
      'employeeNumber'
    ),
    absenceType: scoreColumnByHeaderAndValues(
      column.label,
      sampleValuesByColumn[index],
      COLUMN_SYNONYMS.absenceType,
      'absenceType'
    ),
    fromDate: scoreColumnByHeaderAndValues(
      column.label,
      sampleValuesByColumn[index],
      COLUMN_SYNONYMS.fromDate,
      'date'
    ),
    toDate: scoreColumnByHeaderAndValues(
      column.label,
      sampleValuesByColumn[index],
      COLUMN_SYNONYMS.toDate,
      'date'
    ),
  }))

  const used = new Set<number>()

  function resolveField(
    currentValue: number | null,
    field: keyof typeof scores[number]
  ) {
    if (currentValue !== null) {
      used.add(currentValue)
      return currentValue
    }

    const candidate = [...scores]
      .sort((first, second) => {
        return Number(second[field]) - Number(first[field])
      })
      .find(item => Number(item[field]) >= 20 && !used.has(item.index))

    if (!candidate) return null

    used.add(candidate.index)
    return candidate.index
  }

  return {
    employeeNumberColumn: resolveField(
      suggestedMapping.employeeNumberColumn,
      'employeeNumber'
    ),
    absenceTypeColumn: resolveField(
      suggestedMapping.absenceTypeColumn,
      'absenceType'
    ),
    fromDateColumn: resolveField(suggestedMapping.fromDateColumn, 'fromDate'),
    toDateColumn: resolveField(suggestedMapping.toDateColumn, 'toDate'),
  }
}

function scoreAbsenceTypeMatch(importedValue: string, option: AbsenceTypeOption) {
  const imported = normalizeImportText(importedValue)
  const optionName = normalizeImportText(option.name)

  if (!imported || !optionName) return 0
  if (imported === optionName) return 1
  if (imported.includes(optionName) || optionName.includes(imported)) return 0.8

  const importedWords = importedValue
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
  const optionWords = option.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)

  const commonWords = importedWords.filter(word => optionWords.includes(word)).length
  if (commonWords > 0) {
    return commonWords / Math.max(importedWords.length, optionWords.length)
  }

  return 0
}

function resolveSuggestedAbsenceTypeId(
  suggestionId: string | null | undefined,
  importedValue: string,
  absenceTypes: AbsenceTypeOption[]
) {
  if (suggestionId) {
    const exactMatch = absenceTypes.find(option => option.id === suggestionId)
    if (exactMatch) return exactMatch.id
  }

  const bestMatch = absenceTypes
    .map(option => ({
      id: option.id,
      score: scoreAbsenceTypeMatch(importedValue, option),
    }))
    .sort((first, second) => second.score - first.score)[0]

  if (!bestMatch || bestMatch.score < 0.45) return ''
  return bestMatch.id
}

interface Props {}

export function AbsenceImport(_props: Props) {
  const translations = useTranslate({
    header: 'settings.absence.import.header',
    step: 'settings.absence.import.step',

    stepUpload: 'settings.absence.import.step-upload',
    stepColumns: 'settings.absence.import.step-columns',
    stepTypes: 'settings.absence.import.step-types',
    stepSummary: 'settings.absence.import.step-summary',
    stepUploadDescription: 'settings.absence.import.step-upload-description',
    stepColumnsDescription: 'settings.absence.import.step-columns-description',
    stepTypesDescription: 'settings.absence.import.step-types-description',
    stepSummaryDescription: 'settings.absence.import.step-summary-description',

    fileHint: 'settings.absence.import.file-hint',
    mappingRequired: 'settings.absence.import.mapping-required',

    noRows: 'settings.absence.import.no-rows',
    allTypesMustBeMapped: 'settings.absence.import.all-types-mapped',

    lockWarning: 'settings.absence.import.lock-warning',
    copied: 'settings.absence.import.copied',
    error: 'settings.absence.import.error',
  })

  const onErrorAuto = useOnErrorAuto()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [step, setStep] = useState<number>(STEP_UPLOAD)
  const [fileInputKey, setFileInputKey] = useState(0)

  const form = useForm<AbsenceImportFormValues>({
    resolver: zodResolver(AbsenceImportFormSchema),
    defaultValues,
  })

  const selectedFile = useFormWatch(form, 'selectedFile')
  const sessionId = useFormWatch(form, 'sessionId')
  const sheetName = useFormWatch(form, 'sheetName')
  const headerRow = useFormWatch(form, 'headerRow')
  const columns = useFormWatch(form, 'columns')
  const previewRows = useFormWatch(form, 'previewRows')
  const parseWarnings = useFormWatch(form, 'parseWarnings')
  const columnMapping = useFormWatch(form, 'columnMapping')
  const analysis = useFormWatch(form, 'analysis')
  const typeMappings = useFormWatch(form, 'typeMappings')
  const options = useFormWatch(form, 'options')
  const executeResult = useFormWatch(form, 'executeResult')
  const localError = useFormWatch(form, 'localError')

  const { data: absenceTypesData } = useQuery(ALL_ABSENCE_TYPES_QUERY, {
    onError: onErrorAuto(),
  })

  const [prepareImport, { loading: preparing }] = useMutation(
    PREPARE_ABSENCE_IMPORT_MUTATION,
    {
      onError: onErrorAuto(translations.error),
    }
  )

  const [analyzeImport, { loading: analyzing }] = useMutation(
    ANALYZE_ABSENCE_IMPORT_MUTATION,
    {
      onError: onErrorAuto(translations.error),
    }
  )

  const [executeImport, { loading: executing }] = useMutation(
    EXECUTE_ABSENCE_IMPORT_MUTATION,
    {
      onError: onErrorAuto(translations.error),
    }
  )

  const absenceTypes = useMemo(
    () =>
      (absenceTypesData?.allAbsenceTypes ?? []).map(absenceType => ({
        id: absenceType.id,
        name: absenceType.name,
        externalId: absenceType.externalId,
      })),
    [absenceTypesData?.allAbsenceTypes]
  )

  const stepTitle = {
    [STEP_UPLOAD]: translations.stepUpload,
    [STEP_COLUMNS]: translations.stepColumns,
    [STEP_TYPES]: translations.stepTypes,
    [STEP_SUMMARY]: translations.stepSummary,
  }[step]

  const stepDescription = {
    [STEP_UPLOAD]: translations.stepUploadDescription,
    [STEP_COLUMNS]: translations.stepColumnsDescription,
    [STEP_TYPES]: translations.stepTypesDescription,
    [STEP_SUMMARY]: translations.stepSummaryDescription,
  }[step]

  const requiredColumnMapping = useMemo(() => {
    const employeeNumberColumn = columnMapping.employeeNumberColumn
    const absenceTypeColumn = columnMapping.absenceTypeColumn
    const fromDateColumn = columnMapping.fromDateColumn
    const toDateColumn = columnMapping.toDateColumn

    if (
      employeeNumberColumn === null ||
      absenceTypeColumn === null ||
      fromDateColumn === null ||
      toDateColumn === null
    ) {
      return null
    }

    return {
      employeeNumberColumn,
      absenceTypeColumn,
      fromDateColumn,
      toDateColumn,
    }
  }, [columnMapping])

  const missingTypeMappings = useMemo(
    () => typeMappings.filter(item => item.absenceTypeId.trim() === ''),
    [typeMappings]
  )

  const failureReport = useMemo(() => {
    if (!executeResult) return ''

    const lines = executeResult.rowResults
      .filter(row => row.status !== 'CREATED')
      .map(row => `Row ${row.sourceRow} [${row.status}] ${row.message}`)

    return lines.join('\n')
  }, [executeResult])

  function setLocalError(message: string) {
    form.setValue('localError', message)
  }

  function resetWizard() {
    setStep(STEP_UPLOAD)
    form.reset(defaultValues)
    setFileInputKey(current => current + 1)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  async function handlePrepare() {
    if (!selectedFile) {
      setLocalError(translations.fileHint)
      return
    }

    setLocalError('')
    form.setValue('executeResult', null)

    const response = await prepareImport({
      variables: {
        file: selectedFile,
      },
    }).catch(() => null)

    if (!response) return

    const payload = response.data?.prepareAbsenceImport
    if (!payload) return
    const currentValues = form.getValues()

    form.reset({
      ...currentValues,
      sessionId: payload.sessionId,
      sheetName: payload.sheetName,
      headerRow: payload.headerRow,
      columns: payload.columns,
      previewRows: payload.previewRows,
      parseWarnings: payload.parseWarnings,
      columnMapping: buildSmartColumnMapping(
        payload.columns,
        payload.previewRows,
        {
          employeeNumberColumn: payload.suggestedMapping.employeeNumberColumn,
          absenceTypeColumn: payload.suggestedMapping.absenceTypeColumn,
          fromDateColumn: payload.suggestedMapping.fromDateColumn,
          toDateColumn: payload.suggestedMapping.toDateColumn,
        }
      ),
      analysis: null,
      typeMappings: [],
      options: {
        ...currentValues.options,
        importAnyway: false,
      },
      executeResult: null,
      localError: '',
    })

    setStep(STEP_COLUMNS)
  }

  async function handleAnalyze() {
    if (!sessionId || !requiredColumnMapping) {
      setLocalError(translations.mappingRequired)
      return
    }

    setLocalError('')

    const response = await analyzeImport({
      variables: {
        sessionId,
        columnMapping: requiredColumnMapping,
      },
    }).catch(() => null)

    if (!response) return

    const payload = response.data?.analyzeAbsenceImport
    if (!payload) return
    const currentValues = form.getValues()

    form.reset({
      ...currentValues,
      analysis: payload,
      typeMappings: payload.importedAbsenceTypes.map(item => ({
        importedValue: item.importedValue,
        absenceTypeId: resolveSuggestedAbsenceTypeId(
          item.suggestedAbsenceTypeId,
          item.importedValue,
          absenceTypes
        ),
      })),
      options: {
        ...currentValues.options,
        importAnyway: false,
      },
      executeResult: null,
      localError: '',
    })

    if (payload.noUserMatches) return

    if (payload.validRowsCount === 0) {
      setLocalError(translations.noRows)
      return
    }

    if (payload.importedAbsenceTypes.length === 0) {
      setStep(STEP_SUMMARY)
      return
    }

    setStep(STEP_TYPES)
  }

  function handleTypeMappingNext() {
    if (missingTypeMappings.length > 0) {
      setLocalError(translations.allTypesMustBeMapped)
      return
    }

    setLocalError('')
    setStep(STEP_SUMMARY)
  }

  useEffect(() => {
    if (!analysis || absenceTypes.length === 0 || typeMappings.length === 0) return

    let hasChanges = false
    const nextMappings = typeMappings.map((mapping, index) => {
      if (mapping.absenceTypeId.trim() !== '') return mapping

      const suggestion = analysis.importedAbsenceTypes[index]
      if (!suggestion) return mapping

      const resolvedId = resolveSuggestedAbsenceTypeId(
        suggestion.suggestedAbsenceTypeId,
        suggestion.importedValue,
        absenceTypes
      )
      if (!resolvedId) return mapping

      hasChanges = true
      return {
        ...mapping,
        absenceTypeId: resolvedId,
      }
    })

    if (hasChanges) {
      form.setValue('typeMappings', nextMappings)
    }
  }, [analysis, absenceTypes, form, typeMappings])

  async function handleExecute() {
    if (!sessionId || !requiredColumnMapping || !analysis) return

    if (missingTypeMappings.length > 0) {
      setLocalError(translations.allTypesMustBeMapped)
      return
    }

    if (analysis.createLockActive && !options.importAnyway) {
      setLocalError(translations.lockWarning)
      return
    }

    setLocalError('')

    const absenceTypeMapping = typeMappings.filter(
      item => item.absenceTypeId !== ''
    )

    const response = await executeImport({
      variables: {
        sessionId,
        columnMapping: requiredColumnMapping,
        absenceTypeMapping,
        setApproved: options.setApproved,
        updateBalance: options.updateBalance,
        bypassCreateLocks: analysis.createLockActive ? options.importAnyway : false,
      },
    }).catch(() => null)

    if (!response) return

    const payload = response.data?.executeAbsenceImport
    if (!payload) return

    form.setValue('executeResult', payload)
    form.setValue('sessionId', null)
  }

  async function handleCopyFailures() {
    if (!failureReport) return

    try {
      await navigator.clipboard.writeText(failureReport)
      toast.success(translations.copied)
    } catch {
      toast.error(translations.error)
    }
  }

  function handleDownloadFailures() {
    if (!failureReport) return

    const blob = new Blob([failureReport], {
      type: 'text/plain;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'absence-import-failures.txt'
    document.body.append(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Section header={translations.header}>
      <div className="grid gap-4">
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm">
          <p className="font-medium">
            {translations.step} {step}/4: {stepTitle}
          </p>
          <p className="mt-1 text-stone-600">{stepDescription}</p>
        </div>

        <Activity mode={localError ? 'visible' : 'hidden'}>
          {!!localError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {localError}
            </div>
          )}
        </Activity>

        <Activity mode={step === STEP_UPLOAD ? 'visible' : 'hidden'}>
          <StepUpload
            key={`upload-${fileInputKey}`}
            control={form.control}
            fileInputRef={fileInputRef}
            fileInputKey={fileInputKey}
            selectedFile={selectedFile}
            preparing={preparing}
            onPrepare={handlePrepare}
            onReset={resetWizard}
          />
        </Activity>

        <Activity mode={step === STEP_COLUMNS ? 'visible' : 'hidden'}>
          <StepColumnMapping
            key={`columns-${sessionId ?? 'empty'}`}
            control={form.control}
            columnMapping={columnMapping}
            columns={columns}
            previewRows={previewRows}
            parseWarnings={parseWarnings}
            sheetName={sheetName}
            headerRow={headerRow}
            analysis={analysis}
            analyzing={analyzing}
            canAnalyze={requiredColumnMapping !== null}
            onAnalyze={handleAnalyze}
            onBack={() => setStep(STEP_UPLOAD)}
          />
        </Activity>

        <Activity mode={step === STEP_TYPES && !!analysis ? 'visible' : 'hidden'}>
          {analysis && (
            <StepTypeMapping
              key={`types-${sessionId ?? 'empty'}-${analysis.importedAbsenceTypes
                .map(item => item.importedValue)
                .join('|')}`}
              control={form.control}
              analysis={analysis}
              absenceTypes={absenceTypes}
              typeMappings={typeMappings}
              onNext={handleTypeMappingNext}
              onBack={() => setStep(STEP_COLUMNS)}
            />
          )}
        </Activity>

        <Activity
          mode={step === STEP_SUMMARY && !!analysis ? 'visible' : 'hidden'}
        >
          {analysis && (
            <StepSummary
              form={form}
              analysis={analysis}
              executeResult={executeResult}
              failureReport={failureReport}
              executing={executing}
              missingTypeMappingsCount={missingTypeMappings.length}
              onExecute={handleExecute}
              onBack={() => setStep(STEP_TYPES)}
              onReset={resetWizard}
              onCopyFailures={handleCopyFailures}
              onDownloadFailures={handleDownloadFailures}
            />
          )}
        </Activity>
      </div>
    </Section>
  )
}
